import { useCallback, useState } from 'react'

import { DeleteForever, DoDisturb, Edit } from '@mui/icons-material'
import { Box, Button, IconButton, Tooltip } from '@mui/material'

import ConfirmationModal from '../../components/modals/ConfirmationModal'
import SecretAndVariableModal from '../../components/modals/SecretAndVariableModal'
import DataTable from '../../components/tables/DataTable'
import { Column } from '../../components/tables/interfaces'
import { axiosRepoIntance } from '../../services/api-client'
import { Secret } from '../../services/repo-service'
import { OWNER } from '../../utils/constants/gitea'
import { AddSecretAndVariableForm } from '../../utils/models/secrets-and-variables'


export const initializedSecrets = ['CI_USER', 'CI_USER_TOKEN', 'KUBECONFIG_CONTENT']


const SecretsList: React.FC = () => {

    const columns: Column[] = [
        { id: "name", label: "Name", type: 'string' },
        { id: "created_at", label: "Created At", type: "datetime" },
        { id: "action", label: "Actions", },
    ];

    const [addSecretFormData, setAddSecretFormData] = useState<AddSecretAndVariableForm>({ name: '', value: '' });
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [deleteSelectedSecret, setDeleteSelectedSecret] = useState<string>('')
    const [openAddModal, setOpenAddModal] = useState<boolean>(false);
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const handleAddClick = () => {
        setOpenAddModal(true);
        setAddSecretFormData({ name: '', value: '' });
    }

    const handleAddSecretForm = (form_data: AddSecretAndVariableForm) => {
        console.log('add secret form data: ', form_data);
        createUpdateActionSecrets(form_data);
        setAddSecretFormData({ name: '', value: '' });
    }

    const handleDeleteSecret = (isDelete: boolean) => {
        if (!isDelete) return;

        deleteActionSecret(deleteSelectedSecret);
        setDeleteSelectedSecret('');
    }

    const listRepoActionSecrets = async (query?: { page?: number; limit?: number }) => {
        try {
            // const response = await axiosRepoIntance.repos.repoListActionsSecrets(OWNER, repo_name, query);
            const response = await axiosRepoIntance.orgs.orgListActionsSecrets(OWNER, query);
            // console.log('listRepoActionSecrets', response.data);
            return response.data;
        } catch (error: any) {
            if (error?.response?.status === 404) {
                return alert('Secrets not found');
            }
            console.log('error while fetching secrets list: ', error);
        }
    }

    const createUpdateActionSecrets = async (updateData: AddSecretAndVariableForm) => {
        try {
            // const response = await axiosRepoIntance.repos.updateRepoSecret(OWNER, selectedRepo, updateData.name, { data: updateData.value });
            const response = await axiosRepoIntance.orgs.updateOrgSecret(OWNER, updateData.name, { data: updateData.value });
            console.log('create/update secret: ', response);
            if (response.status === 201 || response.status === 204) {
                setRefreshKey(prevKey => prevKey + 1);
            }
        } catch (error: any) {
            if (error?.response?.status === 400 || error?.response?.status === 404) {
                return alert(error?.response?.data?.message)
            } else if (error?.response?.status === 422) {
                return alert(error?.response?.data?.message)
            }
            console.log('error while create/update secret: ', error)
        }
    }

    const deleteActionSecret = async (name: string) => {
        try {
            // const response = await axiosRepoIntance.repos.deleteRepoSecret(OWNER, selectedRepo, name)
            const response = await axiosRepoIntance.orgs.deleteOrgSecret(OWNER, name);
            console.log('delete secret response: ', response);
            if (response.status === 204) {
                setRefreshKey(prevKey => prevKey + 1);
            }
        } catch (error: any) {
            if (error?.response?.status === 400 || error?.response?.status === 404) {
                return alert(error?.response?.data?.message)
            } else if (error?.response?.status === 422) {
                return alert(error?.response?.data?.message)
            }
            console.log('error while delete secret: ', error)
        }
    }

    const fetchData = useCallback(async (query?: { pageSize?: number; nextPageToken?: string } | { page?: number; limit?: number }) => {
        const response = await listRepoActionSecrets(query as { page?: number; limit?: number });
        console.log('fetched table data: ', response);

        return response || [];
    }, [])

    const dataExtractor = useCallback((response: Secret[]) => {
        return response.map(item => {
            const isDisabled: boolean = initializedSecrets.includes(item.name || '');

            return {
                id: item.name,
                name: item.name,
                created_at: item.created_at,
                action: <Box display='flex' gap={1}>
                    {isDisabled ?
                        <IconButton color='warning' disabled={isDisabled}>
                            <DoDisturb />
                        </IconButton>
                        :
                        <>
                            <Tooltip title='Edit Secret'>
                                <IconButton color='warning' disabled={isDisabled} onClick={() => {
                                    setAddSecretFormData({ name: item.name || '', value: '' });
                                    setOpenAddModal(true);
                                }}>
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title='Delete Secret'>
                                <IconButton color='error' disabled={isDisabled} onClick={() => {
                                    setOpenDeleteModal(true)
                                    setDeleteSelectedSecret(item.name || '')
                                }}>
                                    <DeleteForever />
                                </IconButton>
                            </Tooltip>
                        </>
                    }
                </Box>
            }
        })
    }, [setOpenAddModal])

    return (
        <Box>
            <Box sx={{ m: 2, display: 'flex', justifySelf: 'flex-end' }} >
                <Button variant='contained' onClick={handleAddClick}>
                    add secret
                </Button>
            </Box>

            <Box key={refreshKey}>
                <DataTable
                    columns={columns}
                    fetchData={fetchData}
                    dataExtractor={dataExtractor}
                />

                {openAddModal &&
                    <SecretAndVariableModal
                        open={openAddModal}
                        formData={addSecretFormData}
                        headingTitle='Secret'
                        initializedSecrets={initializedSecrets}
                        setOpen={setOpenAddModal}
                        handleDataSubmit={handleAddSecretForm}
                    />
                }

                {openDeleteModal &&
                    <ConfirmationModal
                        open={openDeleteModal}
                        setOpen={setOpenDeleteModal}
                        headTitle='Delete'
                        bodyMessage='Removing a secret is permanent and cannot be undone. Continue?'
                        confirmBtnColor='error'
                        handleSubmit={handleDeleteSecret}
                    />
                }
            </Box>
        </Box >
    )
}

export default SecretsList